// @flow

import * as React from 'react';
import { useMutation } from 'urql';
import { connect } from 'react-redux';

import Button from '@ta/styleguide.button';
import { localize } from '@ta/common.i18n';

import type { ContentIdType } from '../content/base';
import { contentIdTypeToString, contentIdTypeToNumber } from '../content/base';

import contentVoteMutation from './SubmitContentVote.gql';
import type { SubmitContentVoteVariables } from './SubmitContentVote.gql';
import profileVoteMutation from './SubmitProfileVote.gql';
import type { SubmitProfileVoteVariables } from './SubmitProfileVote.gql';
import styles from './SubmitIapVote.less';

/**
 * The supported content types
 */
const ContentType = Object.freeze({
  FORUM: 'FORUMPOST',
  USERREVIEW: 'USERREVIEW',
  MEDIA: 'MEDIA',
  TRIP: '2',
  LINK: '1',
  REPOST: '3',
  PROFILE: '4',
  LOCATION_TOPIC: 'LOCATION_TOPIC',
  LOCATION_POSTING: 'LOCATION_POSTING',
  SAFETY_MESSAGE: 'SAFETY_MESSAGE',
});

export type ForumInfo = {|
  forumId?: number,
  forumTopicId?: number,
|};

export type RepostInfo = {
  originalId?: ContentIdType,
  originalType?: string,
};

type Props = {|
  /**
   * See ContentType for valid entries.
   * Not using flow enumerated values because I can't use them literally in this definition
   */
  contentType: string,
  /**
   *
   */
  contentId: ContentIdType,
  /**
   * GUID user id, not the numerical member id
   */
  userId: string,
  /**
   * The csrf token used to validate certain voting actions
   */
  csrfToken: string,

  /**
   * An object that contains properties to be submitted for votes
   */
  submissionState: any,

  /**
   * A function to call when the vote was submitted successfully
   */
  onComplete: () => void,
  /**
   *A function to call when the vote failed to submit
   */
  onError: () => void,

  /**
   * Function called when back button is pressed
   */
  onBack: () => void,

  /**
   * Should we show the back button?
   */
  showBackButton: boolean,
  /**
   * Control whether the submit button is sticky. Only applies to mobile.
   * This is due to a bug in IOS/Safari. Refactoring it is a big job, so for now
   * IPhone/Safari forms don't get sticky submit buttons.
   */
  sticky: boolean,
  forumInfo?: ForumInfo,
  repostInfo?: RepostInfo,
|};

const LEGACY_CONTENT = new Set([
  ContentType.USERREVIEW,
  ContentType.MEDIA,
  ContentType.LOCATION_TOPIC,
  ContentType.LOCATION_POSTING,
]);

const FORUM_CONTENT = new Set([ContentType.FORUM]);

const MODERN_CONTENT = new Set([ContentType.LINK, ContentType.TRIP, ContentType.SAFETY_MESSAGE]);

const PROFILE_CONTENT = new Set([ContentType.PROFILE]);

const fetchUrl = (url: string) =>
  fetch(url, {
    cache: 'no-cache',
    credentials: 'same-origin',
    method: 'POST',
    redirect: 'follow',
  });

/**
 * Submit a vote to the ReportIAP servlet. This is only for reviews, photos and video
 */
const submitLegacyVote = (props: Props) => {
  const formData = new URLSearchParams();
  formData.set('token', props.csrfToken);
  formData.set('contentId', `${contentIdTypeToNumber(props.contentId) || ''}`);
  formData.set('voteContentType', props.contentType);

  formData.set('comment', props.submissionState.comment);
  formData.set('reasonId', props.submissionState.iapReason);
  if (props.submissionState.suggestedLocationId) {
    formData.set('suggestedLocationId', props.submissionState.suggestedLocationId);
  }

  return fetchUrl(`/ReportIAP?action=reportIAP&step=SAVE&${formData.toString()}`);
};

/**
 * Submit a vote to the ForumVote servlet.
 */
const submitForumLegacyVote = (props: Props) => {
  const { forumId = -1, forumTopicId = -1 } = props.forumInfo || {};
  const contentId = contentIdTypeToNumber(props.contentId) || '';

  const formBody = new URLSearchParams({
    token: props.csrfToken.toString(),
    fp: contentId.toString(),
    ff: forumId.toString(),
    ft: forumTopicId.toString(),
    reason: props.submissionState.iapReason.toString(),
  });

  return fetch('/ForumVote', {
    cache: 'no-cache',
    credentials: 'same-origin',
    method: 'POST',
    redirect: 'follow',
    accept: '*/*',
    body: formBody,
    headers: {
      Accept: 'text/html, application/xml, text/xml, */*',
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
};

const submitVoteAndClose = (
  inProps: Props,
  contentVote: (data: SubmitContentVoteVariables) => mixed,
  profileVote: (data: SubmitProfileVoteVariables) => mixed,
) => {
  if (!inProps.submissionState) {
    return;
  }

  // Let contentId be converted as it's used. Only convert repostId because we know what it's supposed to be
  // CMI-4102: Object appears to be created regardless of whether it is used, so check for invalid id.
  const { originalId } = inProps.repostInfo || {};
  let contentId;
  let repostId;
  if (originalId && contentIdTypeToNumber(originalId) !== -1) {
    contentId = originalId;
    repostId = contentIdTypeToNumber(inProps.contentId);
  } else {
    // eslint-disable-next-line prefer-destructuring
    contentId = inProps.contentId;
  }

  const props = { ...inProps, contentId };

  if (repostId) {
    contentVote({
      contentId: repostId,
      contentType: ContentType.REPOST,
      userId: props.userId,

      iapReasonId: props.submissionState.iapReason,
      comments: props.submissionState.comment,
    });
  }

  if (LEGACY_CONTENT.has(props.contentType)) {
    submitLegacyVote(props)
      .then(() => {
        props.onComplete();
      })
      .catch(() => {
        props.onError();
      });
  } else if (FORUM_CONTENT.has(props.contentType)) {
    submitForumLegacyVote(props)
      .then(() => {
        props.onComplete();
      })
      .catch(() => {
        props.onError();
      });
  } else if (MODERN_CONTENT.has(props.contentType)) {
    // on complete/onerror are handled by the mutation.
    contentVote({
      contentId: contentIdTypeToNumber(props.contentId),
      contentType: props.contentType,
      userId: props.userId,

      iapReasonId: props.submissionState.iapReason,
      comments: props.submissionState.comment,
    });
  } else if (PROFILE_CONTENT.has(props.contentType)) {
    const reportedUserId = contentIdTypeToString(props.contentId);
    if (reportedUserId) {
      profileVote({
        reportedUserId,
        userId: props.userId,
        iapReasonId: props.submissionState.iapReason,
        comments: props.submissionState.comment,
      });
    }
  }
};

/**
 * Handles the submission of the IAP vote button
 * We're mixing old and new here, so it's a little complex
 * Mutation
 */
const SubmitIAPVoteButton = (props: Props) => {
  let wrapperClass = styles.wrapper;
  if (props.sticky) {
    wrapperClass += ` ${styles.sticky}`;
  }

  /*
    This spacer is for mobile. Position:sticky doesn't work 100% of the time.
    We can get away with position:absolute but we need a space at the bottom. Instead of guessing,
    just render a second button but make it invisible (and don't let it do anything). The space will be taken up
    and you won't be able to cover content with the absolute button.

    Yes it's cheating but these components aren't too expensive. If this submit button gets more complex
    Then it's time to re-work this.
  */
  const stickySpacer = props.sticky ? (
    <div className={styles.stickySpacer}>
      <Button disabled />
    </div>
  ) : null;

  const [, submitProfileVote] = useMutation(profileVoteMutation);
  const [, submitContentVote] = useMutation(contentVoteMutation);

  return (
    <React.Fragment>
      {stickySpacer}
      <div className={wrapperClass}>
        {props.showBackButton ? (
          <Button type="secondary" onClick={props.onBack}>
            {localize('com_fyf_p2_011') // Back
            }
          </Button>
        ) : null}

        <Button
          type="primary"
          disabled={props.submissionState === false}
          onClick={() =>
            submitVoteAndClose(
              props,
              variables =>
                submitContentVote(variables)
                  .then(({ error }) => (error ? props.onError() : props.onComplete()))
                  .catch(props.onError),
              variables =>
                submitProfileVote(variables)
                  .then(({ error }) => (error ? props.onError() : props.onComplete()))
                  .catch(props.onError),
            )
          }
        >
          {localize('common_Submit')}
        </Button>
      </div>
    </React.Fragment>
  );
};

// use this method to connect your panels to the required global state
// $FlowFixMe92
const propConnect = connect(state => ({
  sticky: state.meta.device.viewportCategory === 'MOBILE',
}));

const SubmitIAPVoteButtonConnect = propConnect(SubmitIAPVoteButton);

SubmitIAPVoteButton.defaultProps = {
  forumInfo: undefined,
  repostInfo: undefined,
};

export { SubmitIAPVoteButtonConnect as SubmitIAPVoteButton, ContentType };
